import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import type { ProductWithPrice } from 'types';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  CheckGlyph,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { usePlanCheckout } from '@/components/pricing/usePlanCheckout';

import { useUser } from '@/utils/useUser';

import { COMPLIANCE, ShieldGlyph, useB2bTranslation } from './kit';
import {
  type BillingInterval,
  formatMoney,
  monthlyCents,
  teamAllowances,
  toTeamPlans,
  yearlySavingPercent,
} from './teamPlans';

// The size most teams start with; falls back to the smallest plan.
const DEFAULT_SEATS = 5;

export default function TeamsPricing({
  products,
}: {
  products: ProductWithPrice[];
}) {
  const { t } = useB2bTranslation();
  const router = useRouter();
  // Arabic defaults to Arabic-Indic digits; the page copy uses Latin ones.
  const locale = router.locale === 'ar' ? 'ar-u-nu-latn' : router.locale;
  const plans = useMemo(() => toTeamPlans(products), [products]);
  const [interval, setBillingInterval] = useState<BillingInterval>('year');
  const [selected, setSelected] = useState(() =>
    Math.max(
      0,
      plans.findIndex((plan) => plan.seats === DEFAULT_SEATS),
    ),
  );
  const { subscription } = useUser();
  const { handleCheckout, priceIdLoading, planChangeModal } = usePlanCheckout({
    isUpgrade: true,
  });

  const bestSaving = useMemo(
    () => Math.max(0, ...plans.map(yearlySavingPercent)),
    [plans],
  );

  const plain = (value: number) => new Intl.NumberFormat(locale).format(value);

  if (plans.length === 0) return null;

  const plan = plans[Math.min(selected, plans.length - 1)];
  const price = plan.prices[interval] ?? plan.prices.month ?? plan.prices.year;
  if (!price) return null;

  const currency = price.currency ?? 'usd';
  const money = (cents: number) => formatMoney(cents, currency, locale);
  const perMonth = monthlyCents(price);
  const perSeat = perMonth / plan.seats;
  const monthly = plan.prices.month?.unit_amount;
  const yearlySaving =
    price.interval === 'year' && monthly
      ? monthly * 12 - (price.unit_amount ?? 0)
      : 0;
  const isCurrent = subscription?.prices?.products?.name === plan.name;
  const allowance = teamAllowances(plan.name);

  const perPerson = [
    t('{{count}} words a month on premium models', {
      count: plain(allowance.words),
    }),
    t('Unlimited use of standard models'),
    t('{{count}} image and video credits a month', {
      count: plain(allowance.credits),
    }),
    t('{{count}} characters of text to speech a month', {
      count: plain(allowance.speech),
    }),
    t('Up to {{count}} transcriptions a month', {
      count: plain(allowance.transcriptions),
    }),
  ];

  const perTeam = [
    t('Shared chats, prompts, brand voices and files'),
    t('{{count}} AI agents for your customers', {
      count: plain(allowance.agents),
    }),
    t('Usage for every person, on one bill'),
    t('API access and our MCP server'),
    t('Priority support'),
  ];

  return (
    <section id='pricing' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
          <SectionHeading
            title={t('Priced per team.')}
            tail={t('Not per tool.')}
            lead={t(
              'Every plan includes the whole workspace. Choose how many people need a seat.',
            )}
          />
          <div
            role='radiogroup'
            aria-label={t('Billing period')}
            className={clsxm(
              'inline-flex flex-none self-start rounded-full p-1 lg:self-auto',
              tone.surface,
              tone.line,
            )}
          >
            {(['month', 'year'] as const).map((value) => {
              const active = interval === value;
              return (
                <button
                  key={value}
                  type='button'
                  role='radio'
                  aria-checked={active}
                  onClick={() => setBillingInterval(value)}
                  className={clsxm(
                    'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                    active
                      ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                      : clsxm(
                          tone.body,
                          'hover:text-[#10101c] dark:hover:text-white',
                        ),
                  )}
                >
                  {value === 'month' ? t('Monthly') : t('Yearly')}
                  {value === 'year' && bestSaving > 0 ? (
                    <span
                      className={clsxm(
                        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                        active
                          ? 'bg-emerald-400 text-[#06281c]'
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-300',
                      )}
                    >
                      {t('Save up to {{percent}}%', { percent: bestSaving })}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className='mt-14 grid gap-5 lg:grid-cols-[1.55fr_1fr]'>
          <div
            className={clsxm(
              'rounded-[32px] p-6 sm:p-10',
              tone.surface,
              tone.line,
              'shadow-[0_40px_80px_-50px_rgba(22,12,90,0.45)]',
            )}
          >
            <p
              id='team-size-label'
              className={clsxm('text-sm font-semibold', tone.ink)}
            >
              {t('How many people are on your team?')}
            </p>
            <div
              role='radiogroup'
              aria-labelledby='team-size-label'
              className='mt-4 grid gap-1.5 rounded-2xl bg-[#f3f3f8] p-1.5 dark:bg-white/[0.05]'
              style={{
                gridTemplateColumns: `repeat(${plans.length}, minmax(0, 1fr))`,
              }}
            >
              {plans.map((option, index) => {
                const active = option === plan;
                return (
                  <button
                    key={option.product.id}
                    type='button'
                    role='radio'
                    aria-checked={active}
                    aria-label={t('{{count}} people', { count: option.seats })}
                    onClick={() => setSelected(index)}
                    className={clsxm(
                      'rounded-xl py-3 text-lg font-semibold tabular-nums transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600',
                      active
                        ? 'bg-white text-[#10101c] shadow-[0_6px_16px_-8px_rgba(22,12,90,0.45)] ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/[0.14] dark:text-white dark:ring-white/10'
                        : clsxm(
                            tone.quiet,
                            'hover:text-[#10101c] dark:hover:text-white',
                          ),
                    )}
                  >
                    {plain(option.seats)}
                  </button>
                );
              })}
            </div>

            <div className='mt-9 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between'>
              <div>
                <p className='flex items-baseline gap-2'>
                  <span
                    className={clsxm(
                      'text-[3.5rem] font-semibold leading-none tracking-[-0.04em] tabular-nums sm:text-7xl',
                      tone.ink,
                    )}
                  >
                    {money(perMonth)}
                  </span>
                  <span className={clsxm('text-lg font-medium', tone.quiet)}>
                    {t('/ month')}
                  </span>
                </p>
                <p className={clsxm('mt-3 text-[15px]', tone.body)}>
                  {price.interval === 'year'
                    ? t('Billed yearly as {{total}}', {
                        total: money(price.unit_amount ?? 0),
                      })
                    : t('Billed monthly')}
                  {yearlySaving > 0 ? (
                    <span className='ms-2 font-semibold text-emerald-700 dark:text-emerald-400'>
                      {t('You save {{amount}}', {
                        amount: money(yearlySaving),
                      })}
                    </span>
                  ) : null}
                </p>
                <p
                  className={clsxm('mt-1 text-[15px] font-semibold', tone.ink)}
                >
                  {t('{{price}} per person a month', { price: money(perSeat) })}
                </p>
              </div>

              <div className='flex flex-col gap-2 sm:items-end'>
                <button
                  type='button'
                  onClick={() => handleCheckout(price, plan.product)}
                  disabled={priceIdLoading === price.id}
                  className={clsxm(
                    buttonPrimary,
                    'w-full sm:w-auto disabled:opacity-70',
                  )}
                >
                  {priceIdLoading === price.id ? (
                    <span className='h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white' />
                  ) : null}
                  {isCurrent
                    ? t('Manage your plan')
                    : t('Get Teams for {{count}} people', {
                        count: plain(plan.seats),
                      })}
                  <ArrowGlyph />
                </button>
                <p className={clsxm('text-xs', tone.quiet)}>
                  {t('Secure payment with Stripe · Cancel anytime')}
                </p>
              </div>
            </div>

            <div className='mt-10 grid gap-8 border-t border-[#0f0f28]/[0.07] pt-8 sm:grid-cols-2 dark:border-white/[0.08]'>
              <FeatureList title={t('Every person gets')} items={perPerson} />
              <FeatureList title={t('Your team gets')} items={perTeam} />
            </div>
          </div>

          <Stage
            variant='ink'
            halftoneAt='100% 0%'
            halftoneReach={60}
            className='flex flex-col p-7 sm:p-10'
          >
            <p className='text-sm font-semibold text-primary-300'>
              {t('Enterprise')}
            </p>
            <h3
              className={clsxm(
                styles.display,
                'mt-3 text-[1.75rem] font-semibold leading-[1.15] text-white sm:text-[2rem]',
              )}
            >
              {t('More than 30 people?')}
            </h3>
            <p className='mt-4 text-[15px] leading-7 text-[#b7b7d2]'>
              {t(
                'We’ll put together a plan around your organization, with volume pricing and help getting everyone started.',
              )}
            </p>
            <ul className='mt-8 space-y-3.5'>
              {[
                t('Volume pricing for larger teams'),
                t('Workshops to get everyone started'),
                t('Security documentation for your review'),
                t('A direct line to our team'),
              ].map((item) => (
                <li
                  key={item}
                  className='flex gap-3 text-[15px] leading-6 text-white'
                >
                  <CheckGlyph className='mt-1 text-primary-300' />
                  {item}
                </li>
              ))}
            </ul>
            <div className='pt-10'>
              <Link
                href='/contact'
                className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-[#10101c] transition-colors duration-200 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('Talk to sales')}
                <ArrowGlyph />
              </Link>
            </div>
            <ul className='mt-auto flex flex-wrap gap-2 border-t border-white/10 pt-6 lg:mt-10'>
              {COMPLIANCE.map((name) => (
                <li
                  key={name}
                  className='flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1.5 text-xs font-semibold text-white/80 ring-1 ring-inset ring-white/10'
                >
                  <ShieldGlyph className='h-3.5 w-3.5 text-emerald-300' />
                  {name}
                </li>
              ))}
            </ul>
          </Stage>
        </div>

        <p className={clsxm('mt-8 text-center text-sm', tone.quiet)}>
          {t('Prices in US dollars.')}{' '}
          <Link
            href='/pricing'
            className='font-medium text-primary-700 underline-offset-4 hover:underline dark:text-primary-300'
          >
            {t('See plans for individuals')}
          </Link>
        </p>
      </div>
      {planChangeModal}
    </section>
  );
}

function FeatureList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <p className={clsxm('text-sm font-semibold', tone.ink)}>{title}</p>
      <ul className='mt-4 space-y-3'>
        {items.map((item) => (
          <li
            key={item}
            className={clsxm('flex gap-3 text-[15px] leading-6', tone.body)}
          >
            <CheckGlyph className='mt-1 text-primary-600 dark:text-primary-400' />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
